import { useState, useEffect } from "react";
import { supabase } from "./db";
import * as XLSX from "xlsx";

const sanitizeColumnName = (name) => {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
};

// 👇 TOTO JE KĽÚČOVÁ OPRAVA PRE EXCEL IMPORTER
const SYSTEM_COLUMNS_MAP = {
  brand: "brand",
  produktova_rada: "product_line",
  sukl: "sukl",
  ean_kod: "ean",
  adc_kod: "adc",
  predchodca: "predecessor",
  vyrobca: "manufacturer",
  krajina_povodu: "country_of_origin",
  kratky_text: "short_description",
  hlavny_popis: "description",
  zlozenie_produktu: "ingredients",
  davkovanie: "dosage",
  kod_colneho_sadzobnika: "customs_code",
  sirka_mm: "width_mm",
  vyska_mm: "height_mm",
  hlbka_mm: "depth_mm",
  vaha_brutto_g: "weight_brutto_g",
  vaha_netto_g: "weight_netto_g",
  pocet_ks_v_kartone: "pieces_in_carton",
  pocet_ks_na_palete: "pieces_on_pallet",
  balenie: "packaging",
  hmotnost_celkova: "total_weight",
  cena_vyrobcu: "price",
  _dph: "vat_percent",
  dmoc: "dmoc",
  zalohovany_obal: "returnable_packaging",
  recyklacny_poplatok: "recycling_fee",
  typ_listingu: "listing_type",
  admin_poplatok: "admin_fee",
};

export default function Dashboard({ session, onBack }) {
  const [fileData, setFileData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [categoriesList, setCategoriesList] = useState([]);
  const [globalAttributes, setGlobalAttributes] = useState([]);
  const [categoryAttributes, setCategoryAttributes] = useState([]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 850);
    handleResize();
    window.addEventListener("resize", handleResize);
    fetchInitialData();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchInitialData = async () => {
    const [{ data: catData }, { data: globData }] = await Promise.all([
      supabase
        .from("categories")
        .select("*")
        .order("name", { ascending: true }),
      supabase
        .from("global_attributes")
        .select("*")
        .order("created_at", { ascending: true }),
    ]);
    if (catData) setCategoriesList(catData);
    if (globData) setGlobalAttributes(globData);
  };

  const handleCategorySelect = async (categoryName) => {
    setSelectedCategory(categoryName);
    setFileData([]);
    setErrors([]);
    setMessage("");

    const { data } = await supabase
      .from("category_attributes")
      .select("*")
      .eq("category_name", categoryName)
      .order("created_at");
    if (data) setCategoryAttributes(data);
  };

  const resetCategory = () => {
    setSelectedCategory(null);
    setCategoryAttributes([]);
    setFileData([]);
    setErrors([]);
    setMessage("");
  };

  const downloadTemplate = () => {
    const globalCols = globalAttributes.map((attr) => attr.attribute_name);
    const customCols = categoryAttributes.map((attr) => attr.attribute_name);
    const fullHeaders = ["Kategória na webe", ...globalCols, ...customCols];

    const emptyRow = {};
    fullHeaders.forEach(
      (col) =>
        (emptyRow[col] = col === "Kategória na webe" ? selectedCategory : "")
    );

    const worksheet = XLSX.utils.json_to_sheet([emptyRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Produkty (Vyplniť tu)");

    const allRules = [...globalAttributes, ...categoryAttributes];
    if (allRules.length > 0) {
      const helpData = allRules.map((attr) => ({
        "Názov stĺpca v Exceli": attr.attribute_name,
        "Povinné pole": attr.is_required ? "ÁNO 🔴" : "Nie",
        "Typ poľa":
          attr.attribute_type === "select" ? "Výber zo zoznamu" : "Voľný text",
        "Povolené hodnoty (Presne skopírovať)":
          attr.attribute_type === "select"
            ? attr.options.join(" | ")
            : "Akýkoľvek text",
      }));
      const wsHelp = XLSX.utils.json_to_sheet(helpData);
      wsHelp["!cols"] = [{ wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 50 }];
      XLSX.utils.book_append_sheet(workbook, wsHelp, "Nápoveda k poliam");
    }

    const safeCategoryName = selectedCategory.replace(/[^a-zA-Z0-9]/g, "_");
    XLSX.writeFile(workbook, `Sablona_Import_${safeCategoryName}.xlsx`);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMessage("");
    setErrors([]);
    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = XLSX.read(event.target.result, { type: "binary" });
      const parsedData = XLSX.utils.sheet_to_json(
        workbook.Sheets[workbook.SheetNames[0]]
      );
      validateData(parsedData);
    };
    reader.readAsBinaryString(file);
  };

  const validateData = (data) => {
    let newErrors = [];
    let validData = [];
    const allRules = [...globalAttributes, ...categoryAttributes];

    data.forEach((row, index) => {
      const rowNum = index + 2;
      let rowErrors = [];
      const processedValues = {};
      let hasProductName = false;

      allRules.forEach((attr) => {
        const cellValue = row[attr.attribute_name];
        const isBaseName =
          attr.attribute_name.toLowerCase().includes("názov produktu") ||
          attr.attribute_name.toLowerCase().includes("nazov produktu");

        if (
          attr.is_required &&
          (!cellValue || String(cellValue).trim() === "")
        ) {
          rowErrors.push(`Chýba povinné pole: ${attr.attribute_name}`);
        }

        if (cellValue !== undefined && cellValue !== "") {
          const valStr = String(cellValue).trim();

          if (
            attr.attribute_type === "select" &&
            !attr.options.includes(valStr)
          ) {
            rowErrors.push(
              `Nesprávna hodnota v '${
                attr.attribute_name
              }'. Povolené sú iba: ${attr.options.join(", ")}`
            );
          }

          let rawColName = sanitizeColumnName(attr.attribute_name);
          if (isBaseName) {
            rawColName = "product_name";
            hasProductName = true;
          }

          // Namapovanie na DB stĺpec a konverzia čísel
          const realDbColumn = SYSTEM_COLUMNS_MAP[rawColName] || rawColName;

          if (
            attr.attribute_type === "number" ||
            attr.attribute_type === "price"
          ) {
            processedValues[realDbColumn] = parseFloat(valStr) || null;
          } else {
            processedValues[realDbColumn] = valStr;
          }
        }
      });

      if (!hasProductName && !row["Názov produktu (na webe, max 50 zn.)"])
        rowErrors.push("V Exceli chýba Názov produktu.");

      if (rowErrors.length > 0)
        newErrors.push(`Riadok ${rowNum}: ${rowErrors.join(" | ")}`);
      else {
        validData.push({
          supplier_id: session.user.id,
          category: selectedCategory,
          stock: 0,
          status: "Čaká na schválenie",
          ...processedValues,
        });
      }
    });

    if (newErrors.length > 0) {
      setErrors(newErrors);
      setFileData([]);
    } else {
      setFileData(validData);
      setMessage(
        `Validácia bola úspešná! Pripravených ${validData.length} produktov pre kategóriu "${selectedCategory}".`
      );
    }
  };

  const saveToDatabase = async () => {
    if (fileData.length === 0) return;
    setIsUploading(true);
    setMessage("");

    const { error } = await supabase.from("products").insert(fileData);
    if (error) {
      setErrors(["Chyba databázy: " + error.message]);
    } else {
      try {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("company_name")
          .eq("id", session.user.id)
          .single();
        await supabase
          .from("supplier_logs")
          .insert([
            {
              supplier_email: session.user.email,
              supplier_name: profileData?.company_name || "Neznáma firma",
              action: `Hromadný import: nahratých ${fileData.length} produktov (Kategória: ${selectedCategory})`,
            },
          ]);
      } catch (logErr) {
        console.error("Chyba:", logErr);
      }
      setMessage("✅ Dáta boli úspešne zapísané do systému!");
      setFileData([]);
    }
    setIsUploading(false);
  };

  const s = getStyles(isMobile);

  return (
    <div style={s.container}>
      {!selectedCategory ? (
        <div style={s.glassCard}>
          <button onClick={onBack} style={s.backBtn}>
            ← Späť na výber
          </button>
          <div style={s.headerSection}>
            <div style={s.badge}>📦 Krok 1/2</div>
            <h2 style={s.heading}>Vyberte kategóriu sortimentu</h2>
            <p style={s.subtitle}>
              Kategória sa automaticky priradí všetkým importovaným produktom a
              vygeneruje šablónu na mieru.
            </p>
          </div>
          <div style={s.categoriesGrid}>
            {categoriesList.map((cat) => (
              <div
                key={cat.id}
                onClick={() => handleCategorySelect(cat.name)}
                style={s.categoryCard}
              >
                <div style={s.iconCircle}>
                  <span style={{ fontSize: "28px" }}>{cat.icon}</span>
                </div>
                <span style={s.categoryText}>{cat.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={s.glassCard}>
          <button onClick={resetCategory} style={s.backBtn}>
            ← Zmeniť kategóriu (
            <span style={{ color: "#3b82f6" }}>{selectedCategory}</span>)
          </button>
          <div style={s.headerSection}>
            <div style={s.badge}>📊 Krok 2/2</div>
            <h2 style={s.heading}>Hromadný import: {selectedCategory}</h2>
          </div>
          <div style={s.stepsContainer}>
            <div style={s.stepBox}>
              <div style={s.stepHeader}>
                <div style={s.stepNumber}>1</div>
                <h3 style={s.stepTitle}>Stiahnuť vzor</h3>
              </div>
              <p style={s.stepDesc}>
                Excel šablóna s dynamickými poľami pre túto kategóriu.
              </p>
              <button onClick={downloadTemplate} style={s.primaryButton}>
                📥 Stiahnuť Excel
              </button>
            </div>
            <div style={s.stepBox}>
              <div style={s.stepHeader}>
                <div style={s.stepNumber}>2</div>
                <h3 style={s.stepTitle}>Nahrať vyplnený súbor</h3>
              </div>
              <p style={s.stepDesc}>
                Nahrajte súbor pre kontrolu a bezpečný import do databázy.
              </p>
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileUpload}
                style={s.fileInput}
              />
            </div>
          </div>
          {errors.length > 0 && (
            <div style={s.errorAlert}>
              <h4>🚨 Našli sa chyby v Exceli:</h4>
              <ul>
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          {message && <div style={s.successAlert}>✨ {message}</div>}
          {fileData.length > 0 && (
            <button
              onClick={saveToDatabase}
              disabled={isUploading}
              style={{ ...s.successButton, opacity: isUploading ? 0.7 : 1 }}
            >
              {isUploading
                ? "Zapisujem dáta..."
                : `💾 Zapísať ${fileData.length} produktov`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const getStyles = (isMobile) => ({
  container: { width: "100%", animation: "fadeIn 0.3s ease-in-out" },
  glassCard: {
    backgroundColor: "rgba(255, 255, 255, 0.65)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    padding: isMobile ? "30px 20px" : "50px",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.05)",
  },
  headerSection: { textAlign: "center", marginBottom: "40px" },
  badge: {
    display: "inline-block",
    padding: "6px 14px",
    backgroundColor: "rgba(59, 130, 246, 0.15)",
    color: "#2563eb",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "700",
    marginBottom: "15px",
  },
  heading: {
    margin: "0 0 10px 0",
    fontSize: isMobile ? "28px" : "36px",
    fontWeight: "800",
    color: "#0f172a",
  },
  subtitle: {
    margin: "0 auto",
    fontSize: "16px",
    color: "#64748b",
    maxWidth: "600px",
  },
  backBtn: {
    background: "rgba(255, 255, 255, 0.8)",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    color: "#475569",
    padding: "10px 20px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "30px",
  },
  categoriesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "20px",
  },
  categoryCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "25px 15px",
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    border: "1px solid rgba(255, 255, 255, 0.8)",
    borderRadius: "20px",
    cursor: "pointer",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
  },
  iconCircle: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "15px",
  },
  categoryText: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#1e293b",
    textAlign: "center",
  },
  stepsContainer: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: "30px",
    marginBottom: "30px",
  },
  stepBox: {
    flex: 1,
    padding: "30px",
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.8)",
  },
  stepHeader: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    marginBottom: "15px",
  },
  stepNumber: {
    width: "36px",
    height: "36px",
    borderRadius: "12px",
    backgroundColor: "#eff6ff",
    color: "#3b82f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "800",
  },
  stepTitle: { margin: 0, fontSize: "18px", fontWeight: "700" },
  stepDesc: {
    color: "#64748b",
    fontSize: "14px",
    marginBottom: "25px",
    flexGrow: 1,
  },
  primaryButton: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#0f172a",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  fileInput: {
    width: "100%",
    padding: "14px",
    background: "#ffffff",
    border: "1px dashed #cbd5e1",
    borderRadius: "12px",
    color: "#475569",
    cursor: "pointer",
    fontSize: "14px",
  },
  errorAlert: {
    padding: "20px",
    backgroundColor: "#fef2f2",
    color: "#991b1b",
    borderRadius: "16px",
    marginBottom: "20px",
    border: "1px solid #fecaca",
  },
  successAlert: {
    padding: "20px",
    backgroundColor: "#f0fdf4",
    color: "#166534",
    borderRadius: "16px",
    marginBottom: "20px",
    border: "1px solid #bbf7d0",
    fontWeight: "600",
    fontSize: "15px",
  },
  successButton: {
    width: "100%",
    padding: "18px",
    backgroundColor: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "16px",
    fontSize: "16px",
    fontWeight: "800",
    cursor: "pointer",
    marginTop: "10px",
  },
});
